/**
 * Unified UI Logger
 *
 * Replaces datadogLogs with an OpenTelemetry-backed logger that emits log
 * records via the browser telemetry instance. Falls back to console methods
 * when telemetry is not yet initialized (e.g. in test environments or during
 * early boot before initTelemetry() runs).
 *
 * This module must not import from src/app/ or src/dashboard/ — it lives in
 * src/core/ and is usable from both trees.
 */

import { type AnyValueMap, SeverityNumber } from "@opentelemetry/api-logs";

import { UIErrorPriorityMap } from "@superblocksteam/shared";
import {
  isBrowserTelemetryInitialized,
  getBrowserTelemetryInstance,
  consoleLogAttributes,
} from "@superblocksteam/telemetry/browser";

/** Public interface matching dashboard/utils/logger.ts so callers need no changes. */
interface UILogger {
  debug(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, context?: Record<string, unknown>): void;
  setUIDiagnosticContext(context: Record<string, unknown>): void;
  getUIDiagnosticContext(): Record<string, unknown>;
}

const LOGGER_NAME = "superblocks-ui";

class UILoggerImpl implements UILogger {
  private diagnosticContext: Record<string, unknown> = {};

  /** Merge call-site context with persisted diagnostic context and derive UIErrorPriority. */
  private buildAttributes(context: Record<string, unknown> = {}): AnyValueMap {
    const merged: AnyValueMap = {
      ...this.diagnosticContext,
      ...context,
    } as AnyValueMap;

    const errorType = merged.superblocks_ui_error_type;
    if (
      errorType != null &&
      typeof errorType === "string" &&
      errorType in UIErrorPriorityMap
    ) {
      merged.superblocks_ui_error_priority =
        UIErrorPriorityMap[errorType as keyof typeof UIErrorPriorityMap];
    }

    return merged;
  }

  private emit(
    severityNumber: SeverityNumber,
    consoleFn: (...args: unknown[]) => void,
    message: string,
    context?: Record<string, unknown>,
  ): void {
    const attributes: AnyValueMap = this.buildAttributes(context);

    // Always write to console so developers can see logs in the browser
    // DevTools regardless of whether OTel export is enabled or configured.
    consoleFn(message, consoleLogAttributes(attributes));

    if (isBrowserTelemetryInitialized()) {
      const otelLogger = getBrowserTelemetryInstance().getLogger(LOGGER_NAME);
      otelLogger.emit({ severityNumber, body: message, attributes });
    }
  }

  debug(message: string, context?: Record<string, unknown>): void {
    this.emit(SeverityNumber.DEBUG, console.debug, message, context);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.emit(SeverityNumber.INFO, console.info, message, context);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    this.emit(SeverityNumber.WARN, console.warn, message, context);
  }

  error(message: string, context?: Record<string, unknown>): void {
    this.emit(SeverityNumber.ERROR, console.error, message, context);
  }

  /**
   * Store context that will be injected as attributes on all subsequent log
   * records. Replaces datadogLogs.setGlobalContext().
   */
  setUIDiagnosticContext(context: Record<string, unknown>): void {
    this.diagnosticContext = { ...this.diagnosticContext, ...context };
  }

  /** Return a snapshot of the accumulated diagnostic context. */
  getUIDiagnosticContext(): Record<string, unknown> {
    return { ...this.diagnosticContext };
  }
}

/** Factory for test isolation — tests should use createUILogger() to get a fresh instance. */
export function createUILogger(): UILogger {
  return new UILoggerImpl();
}

/** Singleton for production use. */
export const uiLogger: UILogger = createUILogger();
